import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * Generic function to export data to Excel with dynamic columns
 * @param {Array} data - array of data objects
 * @param {Array} columns - array of column definitions with key and label
 * @param {string} sheetName - name of the sheet
 * @param {string} fileName - name of the downloaded file
 */
export const exportToExcel = (data, columns, sheetName = 'Data', fileName = 'export') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  try {
    // Prepare worksheet data with column headers
    const worksheetData = data.map((item) => {
      const row = {};
      columns.forEach((col) => {
        const key = col.key;
        const label = col.label || key;
        let value = item[key];
        
        // Handle various data types
        if (value === null || value === undefined) {
          row[label] = '';
        } else if (typeof value === 'object') {
          row[label] = Array.isArray(value) ? value.join(', ') : JSON.stringify(value);
        } else {
          row[label] = value.toString();
        }
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    // Auto column width
    const cols = columns.map((col) => {
      const label = col.label || col.key;
      const maxLength = Math.max(
        label.length,
        ...data.map((row) => {
          const value = row[col.key];
          const strValue = value === null || value === undefined ? '' : value.toString();
          return strValue.length;
        })
      );
      return { wch: Math.min(maxLength + 3, 50) }; // cap at 50
    });
    worksheet["!cols"] = cols;

    // Freeze top row
    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

    // Bold headers
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = {
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `${fileName}_${timestamp}.xlsx`);
  } catch (error) {
    console.error('Export failed:', error);
    alert('Failed to export data');
  }
};

/**
 * Export contacts to Excel
 */
export const exportContactsToExcel = (contacts, visibleColumns) => {
  if (!contacts || contacts.length === 0) {
    alert('No contacts to export');
    return;
  }

  // If visibleColumns not provided, use default mapping
  const columns = visibleColumns || [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'companyName', label: 'Company Name' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'department', label: 'Department' },
    { key: 'leadStatus', label: 'Lead Status' },
    { key: 'leadSource', label: 'Lead Source' },
    { key: 'linkedInUrl', label: 'LinkedIn URL' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'createdAt', label: 'Created Date' }
  ];

  exportToExcel(contacts, columns, 'Contacts', 'Contacts');
};

/**
 * Export tasks to Excel
 */
export const exportTasksToExcel = (tasks, visibleColumns) => {
  if (!tasks || tasks.length === 0) {
    alert('No tasks to export');
    return;
  }

  const columns = visibleColumns || [
    { key: 'subject', label: 'Subject' },
    { key: 'type', label: 'Type' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'priority', label: 'Priority' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'status', label: 'Status' },
    { key: 'linkedContact', label: 'Linked Contact' },
    { key: 'linkedAccount', label: 'Linked Account' },
    { key: 'createdAt', label: 'Created At' }
  ];

  exportToExcel(tasks, columns, 'Tasks', 'Tasks');
};

/**
 * Export jobs to Excel
 */
export const exportJobsToExcel = (jobs, visibleColumns) => {
  if (!jobs || jobs.length === 0) {
    alert('No jobs to export');
    return;
  }

  const columns = visibleColumns || [
    { key: 'jobCode', label: 'Job Code' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'client', label: 'Client' },
    { key: 'jobStatus', label: 'Status' },
    { key: 'jobPriority', label: 'Priority' },
    { key: 'numberOfOpenings', label: 'Openings' },
    { key: 'workModel', label: 'Work Model' },
    { key: 'jobType', label: 'Job Type' },
    { key: 'experienceMin', label: 'Min Experience' },
    { key: 'experienceMax', label: 'Max Experience' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'createdAt', label: 'Created At' }
  ];

  exportToExcel(jobs, columns, 'Jobs', 'Jobs');
};

/**
 * Export candidates to Excel
 */
export const exportCandidatesToExcel = (candidates, visibleColumns) => {
  if (!candidates || candidates.length === 0) {
    alert('No candidates to export');
    return;
  }

  const columns = visibleColumns || [
    { key: 'applicationId', label: 'Application ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'primaryEmail', label: 'Email' },
    { key: 'primaryPhone', label: 'Phone' },
    { key: 'currentJobTitle', label: 'Current Job Title' },
    { key: 'totalYearsOfExperience', label: 'Years of Experience' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created At' }
  ];

  exportToExcel(candidates, columns, 'Candidates', 'Candidates');
};

/**
 * Export companies to Excel
 */
export const exportCompaniesToExcel = (companies, visibleColumns) => {
  if (!companies || companies.length === 0) {
    alert('No companies to export');
    return;
  }

  const columns = visibleColumns || [
    { key: 'companyName', label: 'Company Name' },
    { key: 'websiteUrl', label: 'Website' },
    { key: 'industry', label: 'Industry' },
    { key: 'leadStatus', label: 'Lead Status' },
    { key: 'leadSource', label: 'Lead Source' },
    { key: 'primaryContact', label: 'Primary Contact' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'contactOwner', label: 'Contact Owner' },
    { key: 'createdAt', label: 'Create Date' }
  ];

  exportToExcel(companies, columns, 'Companies', 'Companies');
};

// Generic Excel download function
export const downloadToExcel = (rows, headers, fileName = 'Report') => {
  try {
    // Create worksheet with headers and data
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-width columns
    const cols = headers.map((header) => {
      const maxLength = Math.max(
        header.length,
        ...rows.map((row) => {
          const cellValue = row[headers.indexOf(header)];
          return cellValue ? cellValue.toString().length : 0;
        })
      );
      return { wch: Math.min(maxLength + 3, 50) }; // cap at 50 for readability
    });
    worksheet["!cols"] = cols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // Save as Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  } catch (error) {
    console.error('Excel download failed:', error);
    alert('Failed to download Excel file');
  }
};

// Generic CSV download function
export const downloadToCSV = (rows, headers, fileName = 'Report') => {
  try {
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => {
          // Handle quotes and commas in cell values
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(',')
      ),
    ].join('\n');

    // Save as CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${fileName}_${new Date().toISOString().split('T')[0]}.csv`);
  } catch (error) {
    console.error('CSV download failed:', error);
    alert('Failed to download CSV file');
  }
};
